(() => {
  if (window.LeslisRecentlyViewed) return;
  const storageKey = 'Concept:recently-viewed';
  const maximum = 12;
  const instances = new Set();
  let cleared = false;

  function allowed() {
    try {
      return window.Cookiebot?.consent?.preferences === true &&
        window.Shopify?.customerPrivacy?.preferencesProcessingAllowed() === true;
    } catch { return false; }
  }

  function denied() {
    try {
      return (window.Cookiebot?.hasResponse === true && window.Cookiebot.consent?.preferences === false) ||
        window.Shopify?.customerPrivacy?.currentVisitorConsent?.().preferences === 'no';
    } catch { return false; }
  }

  function productId(value) {
    if (!/^\d{1,16}$/.test(String(value))) return null;
    const number = Number(value);
    return Number.isSafeInteger(number) && number > 0 ? number : null;
  }

  function read() {
    if (!allowed() || cleared) return [];
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw || raw.length > 4096) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? [...new Set(parsed.map(productId).filter(Boolean))].slice(0, maximum) : [];
    } catch { return []; }
  }

  function remember(value) {
    const id = productId(value);
    if (!id || !allowed() || cleared) return;
    try { window.localStorage.setItem(storageKey, JSON.stringify([id, ...read().filter(item => item !== id)].slice(0, maximum))); } catch {}
  }

  function clear() {
    cleared = true;
    try { window.localStorage.removeItem(storageKey); } catch {}
    instances.forEach(instance => instance.reset());
  }

  class LeslisRecentlyViewed extends HTMLElement {
    connectedCallback() {
      if (instances.has(this)) return;
      instances.add(this);
      this.content = this.querySelector('[data-recent-content]');
      this.heading = this.querySelector('template[data-recent-heading]');
      this.addEventListener('click', this.onClick = event => {
        if (event.target.closest('[data-recent-clear]')) clear();
      });
      if (allowed()) remember(this.dataset.productId);
      this.refresh();
    }

    disconnectedCallback() {
      instances.delete(this);
      this.controller?.abort();
      this.observer?.disconnect();
      this.removeEventListener('click', this.onClick);
    }

    reset() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.signature = '';
      this.hidden = true;
      this.content?.replaceChildren();
    }

    refresh() {
      if (!allowed() || cleared || !this.content) { this.reset(); return; }
      const current = productId(this.dataset.productId);
      const limit = Math.min(maximum, Math.max(2, Number(this.dataset.limit) || 6));
      const ids = read().filter(id => id !== current).slice(0, limit);
      if (!ids.length) { this.reset(); return; }
      const signature = ids.join(',');
      if (signature === this.signature) return;
      this.reset();
      this.signature = signature;
      this.hidden = false;
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.load(ids, signature);
        }, { rootMargin: '400px 0px' });
        this.observer.observe(this);
      } else this.load(ids, signature);
    }

    async load(ids, signature) {
      if (!allowed() || !this.isConnected) { this.reset(); return; }
      this.controller = new AbortController();
      const controller = this.controller;
      try {
        const url = new URL(this.dataset.searchUrl, window.location.origin);
        if (url.origin !== window.location.origin) { this.reset(); return; }
        url.searchParams.set('section_id', 'leslis-recently-viewed');
        url.searchParams.set('type', 'product');
        url.searchParams.set('q', ids.map(id => `id:${id}`).join(' OR '));
        const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error('Unavailable section');
        const html = await response.text();
        if (controller.signal.aborted || !allowed() || !this.isConnected || signature !== this.signature) return;
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const cards = [...documentFragment.querySelectorAll('[data-recent-product-id]')];
        const matching = new Map(cards.map(card => [productId(card.dataset.recentProductId), card]));
        const ordered = ids.map(id => matching.get(id)).filter(Boolean);
        if (!ordered.length) { this.reset(); return; }
        const list = document.createElement('ul');
        list.className = 'leslis-recently-viewed__list';
        list.setAttribute('role', 'list');
        ordered.forEach(card => list.append(card));
        this.content.replaceChildren(this.heading.content.cloneNode(true), list);
        this.dispatchEvent(new CustomEvent('leslis:recently-viewed-rendered', { bubbles: true }));
      } catch {
        if (!controller.signal.aborted && signature === this.signature) this.reset();
      }
    }
  }

  function consentChanged() {
    if (denied()) { clear(); return; }
    if (allowed()) {
      cleared = false;
      instances.forEach(instance => remember(instance.dataset.productId));
    }
    instances.forEach(instance => instance.refresh());
  }

  customElements.define('leslis-recently-viewed', LeslisRecentlyViewed);
  window.LeslisRecentlyViewed = Object.freeze({ clear, status: () => ({ allowed: allowed(), instances: instances.size, storageKey }) });
  window.addEventListener('storage', event => {
    if (event.key !== storageKey && event.key !== null) return;
    cleared = false;
    instances.forEach(instance => instance.refresh());
  });
  document.addEventListener('visitorConsentCollected', consentChanged);
  ['CookiebotOnConsentReady', 'CookiebotOnAccept', 'CookiebotOnDecline'].forEach(name => window.addEventListener(name, consentChanged));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') instances.forEach(instance => instance.refresh());
  });
  consentChanged();
  if (!window.Shopify?.customerPrivacy && typeof window.Shopify?.loadFeatures === 'function') {
    try { window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => { if (!error) consentChanged(); }); } catch {}
  }
})();
